import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { 
  BookOpen, 
  PlayCircle, 
  FileText, 
  Bell, 
  Clock, 
  ArrowRight,
  Calendar,
  CheckCircle2
} from "lucide-react";

const StudentDashboard = () => {
  const { user } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [enrollmentsRes, assignmentsRes, notificationsRes] = await Promise.all([
        axios.get(`${API}/enrollments`, { withCredentials: true }),
        axios.get(`${API}/assignments`, { withCredentials: true }),
        axios.get(`${API}/notifications`, { withCredentials: true })
      ]);
      
      setEnrollments(enrollmentsRes.data);
      setAssignments(assignmentsRes.data.slice(0, 5)); // Show only 5 upcoming
      setNotifications(notificationsRes.data.filter(n => !n.read).slice(0, 3));
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const getTimeUntilDue = (dueDate) => {
    const now = new Date();
    const due = new Date(dueDate);
    const diff = due - now;
    
    if (diff < 0) return { text: "Overdue", urgent: true };
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return { text: `${days} day${days > 1 ? 's' : ''} left`, urgent: days <= 2 };
    return { text: `${hours} hour${hours > 1 ? 's' : ''} left`, urgent: true };
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-700"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in" data-testid="student-dashboard">
        {/* Welcome Section */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-stone-900" style={{ fontFamily: 'Outfit' }}>
              Welcome back, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-stone-600 mt-1">Continue where you left off</p>
          </div>
          <Link to="/courses">
            <Button className="bg-violet-700 hover:bg-violet-800 rounded-full px-6" data-testid="explore-courses-btn">
              Explore Courses <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="bg-white rounded-xl border border-stone-100 shadow-sm" data-testid="enrolled-courses-stat">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center">
                  <BookOpen className="h-6 w-6 text-violet-700" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">Enrolled Courses</p>
                  <p className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>
                    {enrollments.length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white rounded-xl border border-stone-100 shadow-sm" data-testid="pending-assignments-stat">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                  <FileText className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">Pending Assignments</p>
                  <p className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>
                    {assignments.length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white rounded-xl border border-stone-100 shadow-sm" data-testid="unread-notifications-stat">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <Bell className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">Unread Notifications</p>
                  <p className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>
                    {notifications.length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* My Courses */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-stone-900" style={{ fontFamily: 'Outfit' }}>
                My Courses
              </h2>
              <Link to="/courses" className="text-violet-700 hover:text-violet-800 text-sm font-medium">
                View All
              </Link>
            </div>

            {enrollments.length === 0 ? (
              <Card className="bg-white rounded-xl border border-stone-100">
                <CardContent className="p-8 text-center">
                  <BookOpen className="h-12 w-12 text-stone-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-stone-900 mb-2">No courses yet</h3>
                  <p className="text-stone-500 mb-4">Start your learning journey today</p>
                  <Link to="/courses">
                    <Button className="bg-violet-700 hover:bg-violet-800 rounded-full">
                      Browse Courses
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {enrollments.slice(0, 3).map((enrollment) => (
                  <Link 
                    key={enrollment.enrollment_id} 
                    to={`/courses/${enrollment.course_id}`}
                    data-testid={`course-card-${enrollment.course_id}`}
                  >
                    <Card className="bg-white rounded-xl border border-stone-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer">
                      <CardContent className="p-4 flex gap-4">
                        <div className="w-24 h-24 bg-stone-100 rounded-lg overflow-hidden flex-shrink-0">
                          {enrollment.course?.thumbnail ? (
                            <img 
                              src={enrollment.course.thumbnail} 
                              alt={enrollment.course?.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <BookOpen className="h-8 w-8 text-stone-400" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-stone-900 truncate">
                            {enrollment.course?.title || 'Course'}
                          </h3>
                          <p className="text-sm text-stone-500 mt-1">
                            {enrollment.course?.instructor || 'Instructor'}
                          </p>
                          <div className="mt-3">
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span className="text-stone-500">Progress</span>
                              <span className="font-medium text-stone-900">{enrollment.progress || 0}%</span>
                            </div>
                            <Progress value={enrollment.progress || 0} className="h-2" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Upcoming Assignments */}
            <Card className="bg-white rounded-xl border border-stone-100">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>
                  Upcoming Assignments
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {assignments.length === 0 ? (
                  <div className="text-center py-4">
                    <CheckCircle2 className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
                    <p className="text-sm text-stone-500">No pending assignments</p>
                  </div>
                ) : (
                  assignments.map((assignment) => {
                    const timeInfo = getTimeUntilDue(assignment.due_date);
                    return (
                      <div 
                        key={assignment.assignment_id}
                        className="flex items-start gap-3 p-3 rounded-lg bg-stone-50 hover:bg-stone-100 transition-colors"
                        data-testid={`assignment-${assignment.assignment_id}`}
                      >
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          timeInfo.urgent ? 'bg-orange-100' : 'bg-violet-100'
                        }`}>
                          <Calendar className={`h-5 w-5 ${
                            timeInfo.urgent ? 'text-orange-600' : 'text-violet-700'
                          }`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-stone-900 text-sm truncate">
                            {assignment.title}
                          </p>
                          <p className={`text-xs mt-1 ${
                            timeInfo.urgent ? 'text-orange-600' : 'text-stone-500'
                          }`}>
                            {timeInfo.text}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>

            {/* Recent Notifications */}
            <Card className="bg-white rounded-xl border border-stone-100">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>
                    Notifications
                  </CardTitle>
                  <Link to="/notifications" className="text-violet-700 hover:text-violet-800 text-xs font-medium">
                    View All
                  </Link>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {notifications.length === 0 ? (
                  <div className="text-center py-4">
                    <Bell className="h-8 w-8 text-stone-300 mx-auto mb-2" />
                    <p className="text-sm text-stone-500">No new notifications</p>
                  </div>
                ) : (
                  notifications.map((notification) => (
                    <div 
                      key={notification.notification_id}
                      className="p-3 rounded-lg bg-violet-50 border-l-4 border-violet-700"
                      data-testid={`notification-${notification.notification_id}`}
                    >
                      <p className="font-medium text-stone-900 text-sm">
                        {notification.title}
                      </p>
                      <p className="text-xs text-stone-500 mt-1 line-clamp-2">
                        {notification.message}
                      </p>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default StudentDashboard;
